import InlineTextButtonComponent from '@naturehouse/design-system/components/atoms/inline-text-button/InlineTextButton.create';
import selectedFilterTemplate from '@templates/website/search/partials/_selectedFilter.html.njk';
import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import convertToCurrency from '@naturehouse/nh-essentials/lib/currency/currencyHelper';
import parseStringAsHtml from '@naturehouse/nh-essentials/lib/html/parser';
import type AbstractFilter from '../../../common/search/store/AbstractFilter';
import GTM from '../../../gtm/GTM';
import GTMOverviewFilterRemoveAllModel from '../../../gtm/models/GTMOverviewFilterRemoveAllModel';
import GeoRadiusFilter from '../store/GeoRadiusFilter';
import HouseSearchFilters from '../store/HouseSearchFilters';
import PriceFilter from '../store/PriceFilter';
import SelectedFiltersToFilterMapping from './SelectedFiltersToFilterMapping';

export interface SelectedFiltersBindingInterface {
    selectedFilters: SearchStoreFilter[];
    datesLabel: string;
    flexibleSearchLabel: string;
    priceRangeLabel: string;
    deleteAllFilters: string;
    translations: string;
    pageType: string;
    defaultSearchPageUrl: string;
}

export interface SelectedFiltersContextInterface extends ContextInterface {
    element: HTMLFormElement;
    bindings: SelectedFiltersBindingInterface;
}

export default class SelectedFilters implements Observer {
    readonly #context: SelectedFiltersContextInterface;

    readonly #deleteAllFiltersName: string = 'deleteAllFilters';

    readonly #priceRangeName: string = 'priceRange';

    readonly #countFilters: string[] = ['bedrooms', 'keyword'];

    readonly #radiusFilter: string = 'radius';

    readonly #aggregateFilters: string[] = [
        'themes',
        'facilities',
        'types',
        'locationDescriptions',
        'sustainability',
        'houseRating',
        'natureRating'
    ];

    readonly #translations: Record<string, string>;

    public constructor(context: SelectedFiltersContextInterface) {
        this.#context = context;

        this.#context.bindings.selectedFilters = [];
        this.#translations = JSON.parse(context.bindings.translations);
    }

    public update(): void {
        this.#renderSelectedFilters();
    }

    public onInit(): void {
        HouseSearchFilters.getInstance().forEach((filter) => filter.attach(this));
        this.#renderSelectedFilters();
    }

    #getSelectedFilters(): SearchStoreFilter[] {
        const params = HouseSearchFilters.getInstance().activeParams;

        const translations = this.#translations;

        const filters: SearchStoreFilter[] = [];

        const formatFilter = (name: string, value: string): SearchStoreFilter => {
            const formattedFilter = { name: name, value: value, label: '' };
            if (this.#countFilters.includes(name)) {
                const translation = translations[name];
                formattedFilter.label = `${translation}: ${value}`;
            }

            return formattedFilter;
        };

        const paramKeys = Object.keys(params);
        paramKeys.forEach((key): void => {
            const value = params[key];

            if (this.#countFilters.includes(key)) {
                filters.push(formatFilter(key, value));
            }

            if (this.#aggregateFilters.includes(key)) {
                value.split(',').forEach((aggregateValue) => {
                    const aggregationFilterInputs: HTMLInputElement[] = Array.from(
                        document.querySelectorAll(
                            `[data-name="aggregations"] input[value="${aggregateValue}"]`
                        )
                    );
                    const foundInput = aggregationFilterInputs.find(
                        (input) => input.value === aggregateValue
                    );

                    const label = foundInput ? foundInput.dataset.label || '' : '';
                    filters.push({ name: key, value: aggregateValue, label: label });
                });
            }
        });

        if (paramKeys.includes('minPrice') && paramKeys.includes('maxPrice')) {
            const minPriceString = convertToCurrency(Number(params.minPrice));
            const maxPriceString = convertToCurrency(Number(params.maxPrice));

            filters.push({
                label: `${translations.prices}: ${minPriceString} - ${maxPriceString}`,
                name: this.#priceRangeName,
                value: ''
            });
        }

        if (paramKeys.includes('place') && paramKeys.includes('radius')) {
            const label = this.#translations.radius_x.replace('{radius}', params.radius);
            filters.push({
                label,
                name: this.#radiusFilter,
                value: ''
            });
        }

        if (filters.length > 1) {
            filters.unshift({
                label: this.#context.bindings.deleteAllFilters,
                name: this.#deleteAllFiltersName,
                value: ''
            });
        }

        return filters;
    }

    #renderSelectedFilters(): void {
        this.#context.element.innerHTML = '';
        const filters = this.#getSelectedFilters();

        filters.forEach((filter) => {
            const { name, value, label } = filter;

            if (name === this.#deleteAllFiltersName) {
                this.#renderDeleteAllFiltersComponent(name, label);
                return;
            }

            const element = parseStringAsHtml(
                selectedFilterTemplate.render({ name, value, label }),
                'li'
            );
            const button = element.querySelector('[data-type="selected-filter"]');
            button?.addEventListener('click', this.#handleClickEvent);
            this.#context.element.appendChild(element);
        });
    }

    readonly #handleClickEvent = async (event: Event): Promise<void> => {
        const button: EventTarget | null = event.currentTarget;

        if (!(button instanceof HTMLButtonElement)) {
            return;
        }

        const name: string | undefined = button?.dataset.name;

        if (!name) {
            return;
        }

        const value: string = button?.dataset.value || '';

        const filters = this.#getSelectedFilters();
        const isLastFilter = filters.length === 1;

        this.#clearFilter(name, value, isLastFilter);
    };

    #clearFilter(name: string, value: string, clearSearch = false): void {
        const filters = HouseSearchFilters.getInstance();

        if (name === this.#deleteAllFiltersName) {
            filters.clear();
            GTM.track(GTMOverviewFilterRemoveAllModel.parseToGTMData());
            this.#redirectToSearchPage();
            return;
        }

        if (name === this.#priceRangeName) {
            filters.get(PriceFilter.NAME)?.clear();
        }

        if (name === this.#radiusFilter) {
            this.#handleRadiusFilter(filters.get(GeoRadiusFilter.NAME));
        }

        const map = SelectedFiltersToFilterMapping.map();
        const isAggregation = this.#aggregateFilters.includes(name);
        const callback = map.get(isAggregation ? 'aggregation' : name);

        if (callback) {
            callback(name, value);
        }

        if (clearSearch) {
            this.#redirectToSearchPage();
        }
    }

    #redirectToSearchPage(): void {
        if (this.#context.bindings.pageType !== 'popular') {
            return;
        }

        const currentUrl = new URL(window.location.href);
        window.location.href = `${this.#context.bindings.defaultSearchPageUrl}${currentUrl.search}`;
    }

    #handleRadiusFilter(filter: AbstractFilter | undefined): void {
        const tablet = getComputedStyle(document.documentElement).getPropertyValue('--tablet');
        const tabletMediaQuery = window.matchMedia(tablet);
        const filterButton = document.body.querySelector('[data-open="modal_mobile_aggregations"]');

        if (tabletMediaQuery.matches || filterButton === null) {
            filter?.clear();
            return;
        }

        filterButton.dispatchEvent(new Event('click'));
    }

    readonly #renderDeleteAllFiltersComponent = (name: string, label: string): void => {
        const li = document.createElement('li');
        const button = parseStringAsHtml(
            InlineTextButtonComponent({
                label: label,
                data: {
                    'data-name': name,
                    'data-testid': 'clear-all-filters-button'
                }
            }),
            'button'
        );

        button.addEventListener('click', (event: Event) => this.#handleClickEvent(event));
        li.append(button);
        this.#context.element.appendChild(li);
    };

    static getClassName(): string {
        return 'SelectedFilters';
    }
}
